import React, { ChangeEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Spinner,
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";

import { BreadCrumb, BreadCrumbSelect } from "../../../components/Common";
import {
  getAdminAnalyticsV2,
  resetCitySelectList,
  resetGradeSelectListByCourseLocal,
} from "../../../store/actions";
import {
  AdminAnalyticsByGovernorate,
  AdminAnalyticsRequest,
  AdminAnalyticsResponse,
  UserRoleWiseCount,
} from "../../../interfaces/analytics";
import { RoleEnum } from "../../../enums";
import {
  CitySelect,
  CourseSelect,
  GovernorateSelect,
  GradeSelect,
} from "../../../components";
import Section from "../AdminDashboard/Section";
import { Widget } from "../AdminDashboard/Widgets";

const AdminDashboardV2Page = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { response: adminAnalytics, loading: loadingDashboard } = useSelector(
    (state: any) => state.analytics.adminAnalyticsV2
  );
  const [widgets, setWidgets] = useState<any[]>([]);
  const [dateValue, setDateValue] = useState([
    moment(new Date()).format("yyyy-MM-DD"),
    moment(new Date()).format("yyyy-MM-DD"),
  ]);
  const [filterGovernorateId, setFilterGovernorateId] = useState<string>("");
  const [filterCourseId, setFilterCourseId] = useState<string>("");
  const [filterGradeId, setFilterGradeId] = useState<string>("");
  const [filterCityId, setFilterCityId] = useState<string>("");
  const [query, setQuery] = useState<AdminAnalyticsRequest>({
    from: moment(new Date()).format("yyyy-MM-DD"),
    to: moment(new Date()).format("yyyy-MM-DD"),
  });

  document.title = `${t("documentTitle", { pageName: "dashboard-v2" })}`;

  useEffect(() => {
    dispatch(getAdminAnalyticsV2(query));
  }, []);

  const onChangeDate = (dates: any) => {
    if (dates && dates.length === 2) {
      setDateValue(dates);
      setQuery({
        ...query,
        from: moment(new Date(dates[0])).format("yyyy-MM-DD"),
        to: moment(new Date(dates[1])).format("yyyy-MM-DD"),
      });
      dispatch(
        getAdminAnalyticsV2({
          ...query,
          from: moment(new Date(dates[0])).format("yyyy-MM-DD"),
          to: moment(new Date(dates[1])).format("yyyy-MM-DD"),
        })
      );
    } else {
      setQuery({
        ...query,
        from: "",
        to: "",
      });
      dispatch(
        getAdminAnalyticsV2({
          ...query,
          from: "",
          to: "",
        })
      );
    }
  };

  useEffect(() => {
    if (adminAnalytics) {
      if (!isFirstLoad) setIsFirstLoad(true);
      prepareWidgets(adminAnalytics);
    }
  }, [adminAnalytics, i18n.language]);

  const onChangeGovernorateFilter = (value: string) => {
    setFilterCityId("");
    dispatch(resetCitySelectList());
    setFilterGovernorateId(value);
    setQuery({ ...query, governorateId: value, cityId: "" });
    dispatch(
      getAdminAnalyticsV2({ ...query, governorateId: value, cityId: "" })
    );
  };

  const onChangeCityFilter = (value: string) => {
    setFilterCityId(value);
    setQuery({ ...query, cityId: value });
    dispatch(getAdminAnalyticsV2({ ...query, cityId: value }));
  };

  const onChangeCourseFilter = (value: string) => {
    setFilterGradeId("");
    dispatch(resetGradeSelectListByCourseLocal());
    setFilterCourseId(value);
    setQuery({ ...query, courseId: value, gradeId: "" });
    dispatch(getAdminAnalyticsV2({ ...query, courseId: value, gradeId: "" }));
  };

  const onChangeGradeFilter = (value: string) => {
    setFilterGradeId(value);
    setQuery({ ...query, gradeId: value });
    dispatch(getAdminAnalyticsV2({ ...query, gradeId: value }));
  };

  const getLink = (role: string) => {
    if (role === RoleEnum.STUDENT) return "users/app-users/students";
    if (role === RoleEnum.PARENT) return "users/app-users/parents";
    if (role === RoleEnum.TEACHER) return "users/app-users/teachers";
    if (role === RoleEnum.TEACHER_ADMIN) return "users/cp-users/teachers";
    if (role === RoleEnum.SUPER_ADMIN) return "";
    if (role === RoleEnum.ADMIN) return "";
  };

  const prepareWidgets = (data: AdminAnalyticsResponse) => {
    const results = [];
    results.push({
      id: 1,
      cardColor: "primary",
      label: t("allUsers"),
      badge: "ri-arrow-right-up-line",
      badgeClass: "success",
      counter: data?.totalUsersCount,
      link: "null",
      linkCaption: "null",
      bgcolor: "success",
      icon: "bx bx-group",
    });
    data.roleWiseCount
      .filter((r) => r.role !== "virtual")
      .forEach((rwc: UserRoleWiseCount, index: number) => {
        results.push({
          id: index + 20,
          cardColor: "secondary",
          label: t(rwc.role + "s"),
          badge: "ri-arrow-right-down-line",
          badgeClass: "danger",
          counter: rwc.count,
          link: getLink(rwc.role),
          linkCaption: `${t("view")} ${t(rwc.role + "s")}`,
          bgcolor: "info",
          icon: "bx bx-user",
        });
      });
    results.push({
      id: 2,
      cardColor: "primary",
      label: t("totalSubscriptions"),
      badgeClass: "success",
      counter: data?.totalPurchases,
      link: "null",
      linkCaption: "null",
      bgcolor: "secondary",
      icon: "ri-funds-box-line",
    });
    results.push({
      id: 3,
      cardColor: "info",
      label: t("aiSolutionUsers"),
      badgeClass: "muted",
      counter: data?.totalEligibleUsersForHomeWork,
      link: "null",
      linkCaption: "null",
      bgcolor: "warning",
      icon: "bx bx-brain",
    });
    results.push({
      id: 4,
      cardColor: "info",
      label: t("olympicUsers"),
      badgeClass: "muted",
      counter: data?.totalEligibleUsersForOlympics,
      link: "null",
      linkCaption: "null",
      bgcolor: "danger",
      icon: "ri-trophy-line",
    });
    setWidgets(results);
  };

  const [isFirstLoad, setIsFirstLoad] = useState<boolean>(false);

  return (
    <React.Fragment>
      {loadingDashboard == true && isFirstLoad === false ? (
        <div
          style={{
            justifyContent: "center",
            alignItems: "center",
            display: "flex",
            height: "100vh",
          }}
        >
          <Spinner
            size="sm"
            className="flex-shrink-0 m-5"
            role="status"
          ></Spinner>
        </div>
      ) : (
        <div className="page-content">
          <Container fluid>
            <BreadCrumb
              title={t("dashboard2")}
              pageTitle={t("dashboard")}
              component={
                <BreadCrumbSelect
                  options={[
                    {
                      title: "dashboard",
                      url: "/dashboard",
                      selected: false,
                    },
                    {
                      title: "dashboard2",
                      url: "/dashboard-v2",
                      selected: true,
                    },
                  ]}
                />
              }
            />
            <Row>
              <Col>
                <div className="h-100">
                  <Section
                    showWelcomeMessage="true"
                    value={dateValue}
                    onChange={(value: any) => onChangeDate(value)}
                  />
                  <Row>
                    <Widget data={widgets} />
                  </Row>
                </div>
              </Col>
            </Row>
            <Row>
              <Col xxl={12}>
                <Card>
                  <CardHeader>
                    <h6 className="card-title mb-0">
                      {t("detailedStatistics")} &nbsp;
                      {loadingDashboard && (
                        <Spinner
                          size={"sm"}
                          className="flex-shrink-0"
                          role="status"
                        ></Spinner>
                      )}
                    </h6>
                  </CardHeader>
                  <CardBody>
                    <Row className="g-2 mb-3">
                      <Col sm={3} className="mb-2">
                        <div className="search-box">
                          <CourseSelect
                            value={filterCourseId}
                            placeholder={t("filterByCourse")}
                            type="select"
                            id="courseId"
                            name="courseId"
                            className="form-select"
                            aria-label={t("filterByCourse")}
                            onChange={(event: ChangeEvent<HTMLInputElement>) =>
                              onChangeCourseFilter(event.target.value)
                            }
                          ></CourseSelect>
                        </div>
                      </Col>
                      <Col sm={3} className="mb-2">
                        <div className="search-box">
                          <GradeSelect
                            courseId={filterCourseId}
                            value={filterGradeId}
                            placeholder={t("filterByGrade")}
                            type="select"
                            id="gradeId"
                            name="gradeId"
                            className="form-select"
                            aria-label={t("filterByGrade")}
                            onChange={(event: ChangeEvent<HTMLInputElement>) =>
                              onChangeGradeFilter(event.target.value)
                            }
                          ></GradeSelect>
                        </div>
                      </Col>
                      <Col sm={3} className="mb-2">
                        <div className="search-box">
                          <GovernorateSelect
                            value={filterGovernorateId}
                            placeholder={t("filterByGovernorate")}
                            type="select"
                            id="governorateId"
                            name="governorateId"
                            className="form-select"
                            aria-label={t("filterByGovernorate")}
                            onChange={(event: ChangeEvent<HTMLInputElement>) =>
                              onChangeGovernorateFilter(event.target.value)
                            }
                          ></GovernorateSelect>
                        </div>
                      </Col>
                      <Col sm={3} className="mb-2">
                        <div className="search-box">
                          <CitySelect
                            placeholder={t("filterByCity")}
                            type="select"
                            id="governorateId"
                            name="governorateId"
                            className="form-select"
                            aria-label={t("filterByCity")}
                            onChange={(event: ChangeEvent<HTMLInputElement>) =>
                              onChangeCityFilter(event.target.value)
                            }
                            governorateId={filterGovernorateId}
                            value={filterCityId}
                          ></CitySelect>
                        </div>
                      </Col>
                    </Row>
                    <Row>
                      <Col xl={12}>
                        <div className="table-responsive">
                          <table className="table table-bordered">
                            <thead>
                              <tr>
                                <th rowSpan={2}>{t("governorate")}</th>
                                <th
                                  colSpan={3}
                                  className="text-center stats-table-color-1"
                                >
                                  {t("parents")}
                                </th>
                                <th
                                  colSpan={3}
                                  className="text-center stats-table-color-2"
                                >
                                  {t("teachers")}
                                </th>
                                <th
                                  colSpan={3}
                                  className="text-center stats-table-color-3"
                                >
                                  {t("students")}
                                </th>
                                <th
                                  colSpan={3}
                                  className="text-center stats-table-color-4"
                                >
                                  {t("oneSubscriptionOnly")}
                                </th>
                                <th
                                  colSpan={3}
                                  className="text-center stats-table-color-5"
                                >
                                  {t("twoSubscriptionOnly")}
                                </th>
                                <th
                                  colSpan={3}
                                  className="text-center stats-table-color-6"
                                >
                                  {t("twoPlusSubscriptionOnly")}
                                </th>
                              </tr>
                              <tr>
                                <th className="text-center stats-table-color-1">
                                  {t("male")}
                                </th>
                                <th className="text-center stats-table-color-1">
                                  {t("female")}
                                </th>
                                <th className="text-center stats-table-color-1">
                                  {t("unspecified")}
                                </th>
                                <th className="text-center stats-table-color-2">
                                  {t("male")}
                                </th>
                                <th className="text-center stats-table-color-2">
                                  {t("female")}
                                </th>
                                <th className="text-center stats-table-color-2">
                                  {t("unspecified")}
                                </th>
                                <th className="text-center stats-table-color-3">
                                  {t("male")}
                                </th>
                                <th className="text-center stats-table-color-3">
                                  {t("female")}
                                </th>
                                <th className="text-center stats-table-color-3">
                                  {t("unspecified")}
                                </th>
                                <th className="text-center stats-table-color-4">
                                  {t("male")}
                                </th>
                                <th className="text-center stats-table-color-4">
                                  {t("female")}
                                </th>
                                <th className="text-center stats-table-color-4">
                                  {t("unspecified")}
                                </th>
                                <th className="text-center  stats-table-color-5">
                                  {t("male")}
                                </th>
                                <th className="text-center  stats-table-color-5">
                                  {t("female")}
                                </th>
                                <th className="text-center  stats-table-color-5">
                                  {t("unspecified")}
                                </th>
                                <th className="text-center stats-table-color-6">
                                  {t("male")}
                                </th>
                                <th className="text-center stats-table-color-6">
                                  {t("female")}
                                </th>
                                <th className="text-center stats-table-color-6">
                                  {t("unspecified")}
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {(
                                adminAnalytics as AdminAnalyticsResponse
                              )?.analyticsByGovernorate.map(
                                (abg: AdminAnalyticsByGovernorate) => {
                                  return (
                                    <tr key={abg.id}>
                                      <td>
                                        {i18n.language === "ar"
                                          ? abg.name.ar === "unspecified"
                                            ? t(abg.name.ar)
                                            : abg.name.ar
                                          : abg.name.en === "unspecified"
                                          ? t(abg.name.en)
                                          : abg.name.en}
                                      </td>
                                      <td className="text-center stats-table-color-1">
                                        {abg.parents.male}
                                      </td>
                                      <td className="text-center stats-table-color-1">
                                        {abg.parents.female}
                                      </td>
                                      <td className="text-center stats-table-color-1">
                                        {abg.parents.unspecified}
                                      </td>
                                      <td className="text-center stats-table-color-2">
                                        {abg.teachers.male}
                                      </td>
                                      <td className="text-center stats-table-color-2">
                                        {abg.teachers.female}
                                      </td>
                                      <td className="text-center stats-table-color-2">
                                        {abg.teachers.unspecified}
                                      </td>
                                      <td className="text-center stats-table-color-3">
                                        {abg.students.male}
                                      </td>
                                      <td className="text-center stats-table-color-3">
                                        {abg.students.female}
                                      </td>
                                      <td className="text-center stats-table-color-3">
                                        {abg.students.unspecified}
                                      </td>
                                      <td className="text-center stats-table-color-4">
                                        {abg.students.onePurchaseOnly.male}
                                      </td>
                                      <td className="text-center stats-table-color-4">
                                        {abg.students.onePurchaseOnly.female}
                                      </td>
                                      <td className="text-center stats-table-color-4">
                                        {
                                          abg.students.onePurchaseOnly
                                            .unspecified
                                        }
                                      </td>
                                      <td className="text-center stats-table-color-5">
                                        {abg.students.twoPurchaseOnly.male}
                                      </td>
                                      <td className="text-center stats-table-color-5">
                                        {abg.students.twoPurchaseOnly.female}
                                      </td>
                                      <td className="text-center stats-table-color-5">
                                        {
                                          abg.students.twoPurchaseOnly
                                            .unspecified
                                        }
                                      </td>
                                      <td className="text-center stats-table-color-6">
                                        {abg.students.twoPlusPurchaseOnly.male}
                                      </td>
                                      <td className="text-center stats-table-color-6">
                                        {
                                          abg.students.twoPlusPurchaseOnly
                                            .female
                                        }
                                      </td>
                                      <td className="text-center stats-table-color-6">
                                        {
                                          abg.students.twoPlusPurchaseOnly
                                            .unspecified
                                        }
                                      </td>
                                    </tr>
                                  );
                                }
                              )}
                            </tbody>
                          </table>
                        </div>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Container>
        </div>
      )}
    </React.Fragment>
  );
};

export default AdminDashboardV2Page;
